'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

const InteractionVolumeChart = dynamic(() => import('./InteractionVolumeChart'), { ssr: false });
const ChannelDistributionChart = dynamic(() => import('./ChannelDistributionChart'), { ssr: false });
const TopEnquiriesChart = dynamic(() => import('./TopEnquiriesChart'), { ssr: false });

// Backend integration point: replace with /api/analytics/supervisor-overview
const kpiCards = [
  { label: 'Total Interactions Today', value: '12,842', delta: '+8.4% vs yesterday', positive: true, icon: 'ChatBubbleLeftRightIcon', span: 2 },
  { label: 'Voice', value: '6,412', delta: '49.9% of total', positive: true, icon: 'PhoneIcon', span: 1 },
  { label: 'Digital', value: '6,430', delta: '50.1% of total', positive: true, icon: 'DevicePhoneMobileIcon', span: 1 },
  { label: 'Avg. Handling Time', value: '04:21', delta: '+0:03 vs target', positive: false, icon: 'ClockIcon', span: 1 },
  { label: 'First Contact Resolution', value: '82%', delta: '+2% vs last week', positive: true, icon: 'CheckBadgeIcon', span: 1 },
  { label: 'Active Agents', value: '287 / 310', delta: '7.4% unavailable', positive: false, icon: 'UsersIcon', span: 1 },
  { label: 'Open Cases', value: '1,248', delta: '+42 since 08:00', positive: false, icon: 'ClipboardDocumentListIcon', span: 1 },
  { label: 'SLA Compliance', value: '94.7%', delta: '-1.2% vs target', positive: false, icon: 'ShieldCheckIcon', span: 1 },
];

const agents = [
  { id: 'agent-001', name: 'Nur Aisyah', status: 'Available', interactions: 32, aht: '04:18', fcr: '88%', sentiment: '92%' },
  { id: 'agent-002', name: 'Hafizuddin Razali', status: 'Busy', interactions: 28, aht: '05:02', fcr: '82%', sentiment: '85%' },
  { id: 'agent-003', name: 'Siti Rohaida', status: 'Available', interactions: 35, aht: '03:55', fcr: '91%', sentiment: '94%' },
  { id: 'agent-004', name: 'Ahmad Shahril', status: 'Away', interactions: 19, aht: '04:45', fcr: '79%', sentiment: '88%' },
  { id: 'agent-005', name: 'Nurul Faizah', status: 'Busy', interactions: 41, aht: '03:38', fcr: '93%', sentiment: '96%' },
  { id: 'agent-006', name: 'Mohd Hafiz', status: 'Available', interactions: 26, aht: '04:52', fcr: '81%', sentiment: '83%' },
  { id: 'agent-007', name: 'Lim Bee Eng', status: 'Available', interactions: 38, aht: '04:05', fcr: '87%', sentiment: '90%' },
  { id: 'agent-008', name: 'Rajasegaran M.', status: 'Busy', interactions: 22, aht: '05:30', fcr: '76%', sentiment: '80%' },
];

export default function AnalyticsContent() {
  const [dateRange, setDateRange] = useState('today');

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-700 text-foreground">MyGCC Operations Overview</h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">Supervisor Dashboard · 16 Aug 2026 · Last updated 14:35</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-1.5 text-[12px] border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <button className="btn-ghost px-3 py-1.5 border border-border flex items-center gap-1.5 text-[13px]">
            <Icon name="ArrowDownTrayIcon" size={14} />
            Export
          </button>
        </div>
      </div>

      {/* KPI Bento Grid: 8 cards → 4-col grid, row1: hero(2) + 2 regular, row2: 4 regular */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {kpiCards.map((card, i) => (
          <div
            key={`kpi-${card.label}`}
            className={`card-elevated p-4 ${card.span === 2 ? 'col-span-2' : 'col-span-1'} ${!card.positive && i !== 0 ? 'border-l-2 border-l-amber-400' : ''}`}
          >
            <div className="flex items-start justify-between mb-2">
              <span className="text-[11px] font-600 uppercase tracking-wide text-muted-foreground leading-tight">{card.label}</span>
              <div className={`p-1.5 rounded flex-shrink-0 ${card.positive ? 'bg-secondary' : 'bg-amber-50'}`}>
                <Icon name={card.icon as Parameters<typeof Icon>[0]['name']} size={14} className={card.positive ? 'text-primary' : 'text-amber-600'} />
              </div>
            </div>
            <div className={`font-700 font-tabular text-foreground mb-1 ${card.span === 2 ? 'text-3xl' : 'text-2xl'}`}>{card.value}</div>
            <div className={`text-[11px] font-500 flex items-center gap-1 ${card.positive ? 'text-green-600' : 'text-amber-600'}`}>
              <Icon name={card.positive ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'} size={11} />
              {card.delta}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-4 mb-4">
        {/* Interaction Volume */}
        <div className="col-span-12 xl:col-span-7 card-elevated p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[13px] font-700">Interaction Volume</h3>
              <p className="text-[11px] text-muted-foreground">Hourly breakdown · Voice vs Digital</p>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-primary inline-block" /> Voice</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-accent inline-block" /> Digital</span>
            </div>
          </div>
          <InteractionVolumeChart />
        </div>

        {/* Channel Distribution */}
        <div className="col-span-12 xl:col-span-5 card-elevated p-4">
          <div className="mb-3">
            <h3 className="text-[13px] font-700">Channel Distribution</h3>
            <p className="text-[11px] text-muted-foreground">Today's interactions by channel</p>
          </div>
          <ChannelDistributionChart />
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-12 gap-4">
        {/* Top Enquiries */}
        <div className="col-span-12 xl:col-span-5 card-elevated p-4">
          <div className="mb-3">
            <h3 className="text-[13px] font-700">Top Citizen Enquiries</h3>
            <p className="text-[11px] text-muted-foreground">By category today</p>
          </div>
          <TopEnquiriesChart />
        </div>

        {/* AI Insights */}
        <div className="col-span-12 xl:col-span-3">
          <div className="ai-panel border rounded-md p-4 h-full">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="SparklesIcon" size={15} className="text-primary" />
              <h3 className="text-[13px] font-700 text-primary">CX AI Insights</h3>
              <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded font-600">AI</span>
            </div>
            <div className="space-y-2.5">
              {[
                { text: 'Passport-related enquiries increased 18% compared with the previous week.', type: 'info', action: 'View Details' },
                { text: 'Average handling time for MySejahtera enquiries decreased by 12% after the new knowledge article was published.', type: 'positive', action: 'View Article' },
                { text: '42 interactions today may require supervisor review due to negative sentiment.', type: 'warning', action: 'Review Now' },
                { text: 'Queue wait time in Immigration exceeded 3 minutes for 28 citizens this morning.', type: 'warning', action: 'View Queue' },
              ].map((insight, i) => (
                <div
                  key={`insight-${i}`}
                  className={`p-2.5 rounded border text-[11px] leading-relaxed ${
                    insight.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                    insight.type === 'positive'? 'bg-green-50 border-green-200' : 'bg-white border-border'
                  }`}
                >
                  <div className="flex items-start gap-1.5 mb-1.5">
                    <Icon name={insight.type === 'warning' ? 'ExclamationTriangleIcon' : insight.type === 'positive' ? 'ArrowTrendingUpIcon' : 'InformationCircleIcon'} size={12} className={`mt-0.5 flex-shrink-0 ${insight.type === 'warning' ? 'text-amber-500' : insight.type === 'positive' ? 'text-green-500' : 'text-primary'}`} />
                    <span className="text-foreground">{insight.text}</span>
                  </div>
                  <button onClick={() => toast.info(insight.action)} className="text-primary font-600 hover:underline">{insight.action} →</button>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-ai-border">
              AI Insights · Not automated decisions · Supervisor review required
            </div>
          </div>
        </div>

        {/* Live Agent Table */}
        <div className="col-span-12 xl:col-span-4 card-elevated overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <h3 className="text-[13px] font-700">Live Agent Performance</h3>
              <p className="text-[11px] text-muted-foreground">287 agents active</p>
            </div>
            <button onClick={() => toast.info('Full agent view')} className="text-[11px] text-primary font-600 hover:underline">View All →</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  {['Agent', 'Status', 'Int.', 'AHT', 'FCR', 'Sentiment'].map((h) => (
                    <th key={`ath-${h}`} className="text-left px-3 py-2 font-600 uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {agents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2.5 font-600 text-foreground whitespace-nowrap">{agent.name}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={agent.status} /></td>
                    <td className="px-3 py-2.5 font-tabular font-600 text-foreground">{agent.interactions}</td>
                    <td className="px-3 py-2.5 font-tabular text-muted-foreground">{agent.aht}</td>
                    <td className={`px-3 py-2.5 font-tabular font-600 ${parseInt(agent.fcr) >= 85 ? 'text-green-600' : 'text-amber-600'}`}>{agent.fcr}</td>
                    <td className={`px-3 py-2.5 font-tabular font-600 ${parseInt(agent.sentiment) >= 88 ? 'text-green-600' : 'text-amber-600'}`}>{agent.sentiment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}